/*
  Overlook sticky Add to cart (phones only, product pages).
  A slim bar fixed to the bottom of the screen with the coffee's name, current price and an
  Add to cart button. It shows whenever the page's own Add to cart button is out of view and
  hides when that button is on screen. Tapping it adds the chosen size, grind and quantity
  straight to the cart and opens the cart drawer.
  The theme re-draws the product area when a size is picked, so every check looks the
  button, title and price up again rather than holding on to old copies.
*/
(() => {
  if (document.querySelector('.satc')) return;
  const findBtn = () => document.querySelector('product-info product-form .product-form__submit[name="add"]') ||
    document.querySelector('product-form .product-form__submit[name="add"]');
  if (!findBtn()) return;

  const bar = document.createElement('div');
  bar.className = 'satc';
  bar.setAttribute('aria-hidden', 'true');
  bar.innerHTML = `
    <div class="satc__info">
      <p class="satc__title"></p>
      <p class="satc__price"></p>
    </div>
    <button type="button" class="satc__btn button" tabindex="-1">Add to cart</button>`;
  document.body.appendChild(bar);
  const btn = bar.querySelector('.satc__btn');

  const currentPrice = (info) => {
    const priceEl = info.querySelector('[id^="price-template"]');
    if (!priceEl) return '';
    const onSale = priceEl.querySelector('.price--on-sale');
    const item = onSale
      ? priceEl.querySelector('.price__sale .price-item--sale')
      : priceEl.querySelector('.price__regular .price-item--regular');
    return (item ? item.textContent : priceEl.textContent).replace(/\s+/g, ' ').trim();
  };

  const update = () => {
    const realBtn = findBtn();
    if (!realBtn) return;
    const info = realBtn.closest('product-info') || document;
    const titleEl = info.querySelector('.product__title h1') || info.querySelector('.product__title');
    bar.querySelector('.satc__title').textContent = titleEl ? titleEl.textContent.trim() : '';
    bar.querySelector('.satc__price').textContent = currentPrice(info);
    btn.disabled = realBtn.disabled;
    const label = realBtn.querySelector('span');
    btn.textContent = (label ? label.textContent : realBtn.textContent).trim() || 'Add to cart';

    const r = realBtn.getBoundingClientRect();
    const onScreen = r.bottom > 0 && r.top < window.innerHeight;
    const show = !onScreen && window.innerWidth < 750;
    bar.classList.toggle('is-visible', show);
    bar.setAttribute('aria-hidden', String(!show));
    btn.tabIndex = show ? 0 : -1;
    document.body.classList.toggle('satc-visible', show);
    if (show !== lastShow) {
      lastShow = show;
      liftFloaters(show);
    }
  };
  let lastShow = false;

  // Chat, email popup and similar app buttons pinned to the bottom corner would sit on top of the bar,
  // so they are nudged up while the bar shows and put back when it hides. These apps often appear a
  // few seconds after the page loads and re-draw themselves, so the check repeats while the bar shows.
  // The independent "translate" property is used so it never fights the app's own transform animations.
  const LIFT = 76;
  const lifted = new Set();
  const unlift = (el) => {
    el.style.removeProperty('translate');
    lifted.delete(el);
  };
  const isFloater = (el) => {
    if (!el.isConnected || el === bar || bar.contains(el) || el.tagName === 'DIALOG' || el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return false;
    const cs = getComputedStyle(el);
    if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') return false;
    const r = el.getBoundingClientRect();
    // Full-size popups and drawers are left alone; only small corner buttons and tabs move.
    if (!r.width || !r.height || r.height > window.innerHeight * 0.5) return false;
    const bottom = r.bottom + (lifted.has(el) ? LIFT : 0);
    return window.innerHeight - bottom <= LIFT;
  };
  const liftFloaters = (show) => {
    if (!show) {
      [...lifted].forEach(unlift);
      return;
    }
    // Some apps (Shopify Forms, for one) keep their floating button inside a shadow root.
    const candidates = [...document.querySelectorAll('body > *, body > * > *, body > * > * > *')];
    candidates.slice().forEach((el) => {
      if (!el.shadowRoot) return;
      [...el.shadowRoot.children].forEach((child) => candidates.push(child, ...child.children));
    });
    [...lifted].forEach((el) => { if (!isFloater(el)) unlift(el); });
    candidates.forEach((el) => {
      if (!isFloater(el)) return;
      if (el.style.getPropertyValue('translate') === '') el.style.setProperty('translate', `0 -${LIFT}px`, 'important');
      lifted.add(el);
    });
  };
  setInterval(() => { if (lastShow) liftFloaters(true); }, 700);

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => { queued = false; update(); }, 60);
  };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  new MutationObserver(schedule).observe(document.querySelector('main') || document.body, {
    subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled'],
  });

  // Add to cart directly instead of pressing the page's button. The product options app (Easify)
  // takes over that button, and pressing it from code did not add anything, which is why the bar
  // did nothing on the live store. This sends the same thing the page would: the chosen size, quantity,
  // grind and any subscription plan, then opens the cart drawer.
  const scrollToBuyBox = () => {
    const realBtn = findBtn();
    if (realBtn) realBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const collect = () => {
    const realBtn = findBtn();
    const form = realBtn && realBtn.closest('product-form') && realBtn.closest('product-form').querySelector('form');
    if (!form) return null;
    const scope = realBtn.closest('product-info') || realBtn.closest('section') || document;
    const id = (form.querySelector('[name="id"]') || {}).value;
    const qtyInput = document.querySelector(`input[name="quantity"][form="${form.id}"]`) || form.querySelector('[name="quantity"]');
    const quantity = Math.max(1, parseInt((qtyInput && qtyInput.value) || '1', 10) || 1);
    const properties = {};
    let missingRequired = false;
    scope.querySelectorAll('[name^="properties["]').forEach((el) => {
      if (el.closest('.oqa')) return;
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
      const key = el.name.slice('properties['.length, -1);
      if (el.value !== '') properties[key] = el.value;
    });
    // A required option left empty: send the shopper to the options instead of adding.
    scope.querySelectorAll('.tpo_option-container').forEach((box) => {
      if (!box.querySelector('.tpo_option-required-indicator') || box.closest('.tpo_hidden')) return;
      const input = box.querySelector('[name^="properties["]');
      if (input && input.value === '') missingRequired = true;
    });
    const plan = form.querySelector('[name="selling_plan"]') || scope.querySelector('[name="selling_plan"]');
    const body = { id: Number(id), quantity, properties };
    if (plan && plan.value) body.selling_plan = Number(plan.value);
    return { body, missingRequired };
  };

  btn.addEventListener('click', async () => {
    const realBtn = findBtn();
    if (!realBtn || realBtn.disabled || btn.disabled) return;
    const item = collect();
    if (!item || !item.body.id || item.missingRequired) { scrollToBuyBox(); return; }
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { ...item.body };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    btn.disabled = true;
    btn.classList.add('is-loading');
    try {
      const res = await fetch(`${(window.routes && routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      if (cart && typeof cart.renderContents === 'function' && data.sections) {
        cart.classList.remove('is-empty');
        cart.renderContents(data);
      } else {
        window.location.href = (window.routes && routes.cart_url) || '/cart';
      }
    } catch (error) {
      // Something the page needs to show (sold out, a limit): take the shopper to the main button.
      scrollToBuyBox();
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = realBtn.disabled;
    }
  });

  update();
})();
